import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock, FaPaperPlane, FaWhatsapp } from "react-icons/fa";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { COMPANY } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api`;

const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", subject: "", message: "" });
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.message) {
            toast.error("Please fill all required fields.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Thank you! We'll get in touch shortly.");
            setForm({ name: "", email: "", phone: "", organization: "", subject: "", message: "" });
        } catch (err) {
            toast.error("Submission failed. Please try WhatsApp or call us.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative py-20 sm:py-28">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center max-w-2xl mx-auto mb-14">
                    <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-600 dark:text-brand-300 mb-4">Contact</div>
                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white">
                        Get in <span className="text-gradient-brand">Touch</span>
                    </h2>
                    <p className="mt-4 text-slate-600 dark:text-slate-300">Reach us for sales enquiries, AMC quotations or urgent service requests.</p>
                </motion.div>

                <div className="grid lg:grid-cols-5 gap-8">
                    <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:col-span-2 space-y-4">
                        <div className="p-6 rounded-3xl bg-gradient-to-br from-brand-700 to-brand-500 text-white shadow-xl">
                            <h3 className="font-heading text-xl font-bold">Company Details</h3>
                            <div className="mt-5 space-y-4 text-sm">
                                <div className="flex gap-3">
                                    <FaMapMarkerAlt className="flex-shrink-0 mt-0.5 text-sky-200" />
                                    <span>{COMPANY.address}</span>
                                </div>
                                <a href={`tel:${COMPANY.phoneDigits}`} data-testid="contact-phone-link" className="flex gap-3 hover:text-sky-200 transition">
                                    <FaPhoneAlt className="flex-shrink-0 mt-0.5 text-sky-200" /> {COMPANY.phone}
                                </a>
                                <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="flex gap-3 hover:text-sky-200 transition break-all">
                                    <FaEnvelope className="flex-shrink-0 mt-0.5 text-sky-200" /> {COMPANY.email}
                                </a>
                                <div className="flex gap-3">
                                    <FaClock className="flex-shrink-0 mt-0.5 text-sky-200" /> Mon–Sat · 9:00 AM – 7:00 PM
                                </div>
                            </div>
                            <a
                                href={`https://wa.me/${COMPANY.phoneDigits}`}
                                target="_blank" rel="noopener noreferrer"
                                data-testid="contact-whatsapp-btn"
                                className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-brand-700 font-semibold text-sm hover:bg-emerald-50 transition"
                            >
                                <FaWhatsapp className="text-emerald-500" /> Chat on WhatsApp
                            </a>
                        </div>

                        <div className="rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm">
                            <iframe
                                title="Ezhil Biomedical Location"
                                data-testid="contact-map-iframe"
                                src="https://www.google.com/maps?q=Karuvadikuppam,Puducherry+605008&output=embed"
                                width="100%"
                                height="260"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </motion.div>

                    <motion.form
                        onSubmit={submit}
                        data-testid="contact-form"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        className="lg:col-span-3 p-7 sm:p-9 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm"
                    >
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">Name *</label>
                                <Input data-testid="contact-input-name" value={form.name} onChange={set("name")} className="mt-1.5" placeholder="Your full name" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">Phone *</label>
                                <Input data-testid="contact-input-phone" value={form.phone} onChange={set("phone")} className="mt-1.5" placeholder="+91 ..." />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">Email *</label>
                                <Input data-testid="contact-input-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5" placeholder="you@example.com" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">Organization</label>
                                <Input data-testid="contact-input-org" value={form.organization} onChange={set("organization")} className="mt-1.5" placeholder="Hospital / Clinic" />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">Subject</label>
                                <Input data-testid="contact-input-subject" value={form.subject} onChange={set("subject")} className="mt-1.5" placeholder="What's this about?" />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">Message *</label>
                                <Textarea data-testid="contact-input-message" rows={5} value={form.message} onChange={set("message")} className="mt-1.5" placeholder="Tell us about your requirement" />
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="contact-submit-btn"
                            className="mt-6 inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-3.5 rounded-full bg-gradient-to-r from-brand-700 to-brand-500 text-white font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                        >
                            {loading ? "Sending..." : <>Send Message <FaPaperPlane /></>}
                        </button>
                    </motion.form>
                </div>
            </div>
        </section>
    );
};

export default Contact;
